'use client';

import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import ActivityTableToolbar from '../Tables/components/ActivityTable/ActivityTableToolbar';
import { useAuth } from 'src/context/auth';
import { useSite } from 'src/context/providers/SiteProvider';
import { TableFooter } from '../Tables';
import { TableColumnMenu } from '../Tables/components/TableColumnMenu';
import {
  PulseHeader,
  PulseColumnMenu,
  PulseLoading,
  PulseColumnDef,
} from 'src/components';
import { useGetActivitiesQuery, useGetActivityTypesQuery, useGetActivityCategoriesQuery, useGetCompanySettingsQuery, useGetAllStatusesQuery } from 'src/api/redux';
import { COMPANY_SETTINGS_IDS } from 'src/api/redux/settings/constants';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import useLocalStorage from 'src/hooks/system/useLocalStorage';
import { ActivityTableRow, ActivityExport } from './components';
import { SortSection } from './components/SortSection';
import { useIntegrations } from 'src/components/Settings/Integrations/context';

const Activity = () => {
  const { user } = useAuth();
  const isManager =
    user?.permissions?.is_manager || user?.permissions?.is_superuser;


  const { hasPermission } = useAuthorization();
  const canReadCategories = hasPermission('read.activity_categories.company');
  const { data: companySettings } = useGetCompanySettingsQuery(undefined, {
    skip: !user?.company?.id,
  });
  const enabledSetting = companySettings?.results?.find(
    (s) => s.code === COMPANY_SETTINGS_IDS.ACTIVITY_CATEGORY_ENABLED
  );
  const isCategoryEnabled = enabledSetting?.value === 'true';
  const labelSetting = companySettings?.results?.find(
    (s) => s.code === COMPANY_SETTINGS_IDS.ACTIVITY_CATEGORY_LABEL
  );
  const categoryColumnLabel = labelSetting?.value || 'Category';
  const showCategoryColumn = isCategoryEnabled && canReadCategories;

  const { mappings } = useIntegrations();
  const showStatusColumn = mappings?.length > 0;
  const { closeAllButCurrent } = useDrawer();
  const { dispatch } = useSite();

  const [sort, setSort] = useState<{
    sort: string | null;
    order: 'asc' | 'desc';
  }>({
    sort: 'created_at',
    order: 'desc',
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedReps, setSelectedReps] = useState<string[] | null>([]);
  const [selectedActivities, setSelectedActivities] = useState<string[] | null>(
    []
  );
  const [selectedCategories, setSelectedCategories] = useState<string[] | null>(
    []
  );
  const [selectedStatuses, setSelectedStatuses] = useState<{
    [key: string]: string[];
  }>({});
  const [selectedTimeframe, setSelectedTimeframe] = useState<string | null>();

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    'activityTable',
    {
      type: true,
      category: true,
      name: true,
      address: true,
      city: true,
      state: true,
      owner: true,
      activity_date: true,
      created_at: true,
      status: true,
      details: true,
    }
  );

  // Define columns using PulseColumnDef
  const columns: PulseColumnDef[] = useMemo(() => {
    const baseColumns: PulseColumnDef[] = [
      {
        field: 'type',
        headerName: 'Activity Type',
        width: 180,
        sortable: true,
        resizable: true,
      },
      ...(showCategoryColumn
        ? [
            {
              field: 'category',
              headerName: categoryColumnLabel,
              width: 150,
              sortable: false,
              resizable: true,
            },
          ]
        : []),
      {
        field: 'name',
        headerName: 'Target',
        width: 220,
        sortable: true,
        resizable: true,
      },
      {
        field: 'address',
        headerName: 'Address',
        width: 200,
        sortable: false,
        resizable: true,
      },
      {
        field: 'city',
        headerName: 'City',
        width: 130,
        sortable: true,
        resizable: true,
      },
      {
        field: 'state',
        headerName: 'State',
        width: 80,
        sortable: true,
        resizable: true,
      },
    ];

    if (isManager) {
      baseColumns.push({
        field: 'owner',
        headerName: 'Representative',
        width: 180,
        sortable: false,
        resizable: true,
      });
    }

    baseColumns.push({
      field: 'activity_date',
      headerName: 'Activity Date',
      width: 150,
      sortable: true,
      resizable: true,
    });

    baseColumns.push({
      field: 'created_at',
      headerName: 'Created At',
      width: 150,
      sortable: true,
      resizable: true,
    });

    if (showStatusColumn) {
      baseColumns.push({
        field: 'status',
        headerName: 'CRM Status',
        width: 180,
        sortable: false,
        resizable: true,
      });
    }

    baseColumns.push({
      field: 'details',
      headerName: 'Note',
      width: 300,
      maxWidth: 9999,
      sortable: false,
      resizable: true,
    });

    return baseColumns;
  }, [isManager, showStatusColumn, showCategoryColumn, categoryColumnLabel]);

  // Filter columns based on visibility
  const visibleColumns = useMemo(() => {
    return columns.filter((col) => columnVisibilityModel[col.field] !== false);
  }, [columns, columnVisibilityModel]);

  // Use column resizing hook
  const { startResizing, isResizing, getGridTemplateColumns } =
    usePulseTableColumns({ columns: visibleColumns });

  const { data, isLoading, isFetching } = useGetActivitiesQuery({
    reps: selectedReps?.length > 0 ? selectedReps : null,
    activityTypes: selectedActivities?.length > 0 ? selectedActivities : null,
    categories: selectedCategories?.length > 0 ? selectedCategories : null,
    statuses:
      selectedStatuses && Object.keys(selectedStatuses).length > 0
        ? selectedStatuses
        : null,
    timeframe: selectedTimeframe,
    sort: sort?.sort,
    order: sort?.order,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    q: searchTerm,
  });

  const activities = useMemo(() => data?.results || [], [data]);

  const { data: allStatuses } = useGetAllStatusesQuery();

  // Fetch activity types once at parent level to avoid per-row hook calls
  const { data: activityTypesData } = useGetActivityTypesQuery();
  const activityTypesMap = useMemo(() => {
    const map = new Map<number, MedScout.ActivityType>();
    activityTypesData?.results?.forEach((type) => {
      map.set(type.id, type);
    });
    return map;
  }, [activityTypesData]);

  const { data: activityCategoriesData } = useGetActivityCategoriesQuery(
    undefined,
    { skip: !showCategoryColumn }
  );
  const activityCategoriesMap = useMemo(() => {
    const map = new Map<number, string>();
    activityCategoriesData?.results?.forEach((cat) => {
      map.set(cat.id, cat.label);
    });
    return map;
  }, [activityCategoriesData]);

  const handleSortToggle = useCallback(
    (columnField: string, order?: 'asc' | 'desc') => {
      setSort((prevSort) => {
        const newOrder =
          order ||
          (prevSort?.sort === columnField
            ? prevSort.order === 'asc'
              ? 'desc'
              : 'asc'
            : 'desc');
        return { sort: columnField, order: newOrder };
      });
      setOpenMenuId(null);
    },
    []
  );

  const handleEllipsisClick = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.stopPropagation();
      setOpenMenuId((prev) => (prev === columnId ? null : columnId));
    },
    []
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handlePageChange = (e: unknown, newPage: number) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPaginationModel({
      pageSize: parseInt(e.target.value, 10),
      page: 0,
    });
  };

  const handleOpenProfile = ({
    providerId,
    type,
  }: {
    providerId: string;
    type: string;
  }) => {
    if (!providerId || !type) return;
    dispatch({
      type: 'SET_CURRENT_PROVIDER',
      payload: { id: providerId, type: type || null },
    });
    closeAllButCurrent('profile');
  };

  const hasActivities = activities && activities.length > 0;
  const showLoadingOverlay = isLoading || isFetching;

  // Virtualization constants and state
  const ROW_HEIGHT = 48; // Height of each row in pixels (matches PulseCell h-12)
  const BUFFER_ROWS = 5; // Number of extra rows to render above/below viewport
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  // Calculate visible rows based on scroll position
  const { visibleRows, startIndex, totalHeight } = useMemo(() => {
    if (!hasActivities) {
      return { visibleRows: [], startIndex: 0, totalHeight: 0 };
    }

    const totalHeight = activities.length * ROW_HEIGHT;
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS
    );
    const visibleCount =
      Math.ceil(containerHeight / ROW_HEIGHT) + BUFFER_ROWS * 2;
    const endIndex = Math.min(activities.length, startIndex + visibleCount);
    const visibleRows = activities.slice(startIndex, endIndex);

    return { visibleRows, startIndex, totalHeight };
  }, [activities, scrollTop, containerHeight, hasActivities]);

  // Handle scroll events
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  // Measure container height on mount and resize
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateHeight = () => {
      setContainerHeight(container.clientHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className="h-full w-full bg-white overflow-hidden flex flex-col">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="p-4 border-b border-neutral-200 bg-white flex justify-between items-center">
          <h4 className="text-2xl font-semibold text-primary">
            <Icon icon={faBolt} className="text-tertiary" /> Activity
          </h4>
          <div className="flex items-center gap-2">
            <TableColumnMenu
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              table="activity"
            />
            <ActivityExport
              disabled={isLoading || !hasActivities}
              exportSearchParams={{
                reps: selectedReps?.length > 0 ? selectedReps : null,
                activityTypes:
                  selectedActivities?.length > 0 ? selectedActivities : null,
                categories:
                  selectedCategories?.length > 0 ? selectedCategories : null,
                statuses:
                  selectedStatuses && Object.keys(selectedStatuses).length > 0
                    ? selectedStatuses
                    : null,
                timeframe: selectedTimeframe,
                sort: sort?.sort,
                order: sort?.order,
                page: paginationModel.page,
                pageSize: paginationModel.pageSize,
                q: searchTerm,
              }}
            />
          </div>
        </div>
        <ActivityTableToolbar
          onSearch={handleSearch}
          selectedReps={selectedReps}
          setSelectedReps={setSelectedReps}
          selectedActivities={selectedActivities}
          setSelectedActivities={setSelectedActivities}
          selectedStatuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          selectedTimeframe={selectedTimeframe}
          setSelectedTimeframe={setSelectedTimeframe}
          statuses={allStatuses}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          showCategoryFilter={showCategoryColumn}
          categoryLabel={categoryColumnLabel}
        />
      </div>

      {/* Table Container */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-auto relative"
        onScroll={handleScroll}
      >
        {/* Header Row - Fixed at top */}
        <div
          className="sticky top-0 z-20 grid min-w-max"
          style={{ gridTemplateColumns: getGridTemplateColumns() }}
        >
          {visibleColumns.map((col) => {
            const isMenuOpen = openMenuId === col.field;
            const isActiveSortColumn = sort?.sort === col.field;

            return (
              <PulseHeader
                key={col.field}
                label={col.headerName}
                isSticky
                stickyPosition="top"
                className="bg-neutral-50 text-neutral-800 border-b border-r border-b-neutral-200 border-r-neutral-200"
                sortable={col.sortable}
                sortDirection={isActiveSortColumn ? sort.order : null}
                resizable={col.resizable}
                onStartResize={
                  col.resizable ? (e) => startResizing(col.field, e) : undefined
                }
                isResizing={isResizing}
                menu={
                  col.sortable ? (
                    <PulseColumnMenu
                      columnId={col.field}
                      isOpen={isMenuOpen}
                      onToggle={(e) => handleEllipsisClick(e, col.field)}
                      customSectionComponents={[
                        <SortSection
                          key="sort"
                          currentOrder={sort?.order || null}
                          isActive={isActiveSortColumn}
                          onChange={(order) =>
                            handleSortToggle(col.field, order)
                          }
                        />,
                      ]}
                    />
                  ) : undefined
                }
              />
            );
          })}
          {/* Filler Header - matches PulseHeader height */}
          <div className="h-12 bg-neutral-50 border-b border-b-neutral-200" />
        </div>

        {/* Virtualized Data Rows */}
        {hasActivities && (
          <div
            role="table"
            aria-label="Activity table"
            className="relative min-w-max"
            style={{ height: totalHeight }}
          >
            {visibleRows.map((activity, index) => (
              <div
                key={activity.id}
                className="absolute left-0 right-0 grid group"
                style={{
                  top: (startIndex + index) * ROW_HEIGHT,
                  height: ROW_HEIGHT,
                  gridTemplateColumns: getGridTemplateColumns(),
                }}
              >
                <ActivityTableRow
                  activity={activity}
                  activityType={activityTypesMap.get(Number(activity.type))}
                  categoryLabel={
                    showCategoryColumn && activity.category
                      ? activityCategoriesMap.get(activity.category)
                      : undefined
                  }
                  showCategoryColumn={showCategoryColumn}
                  isManager={isManager}
                  showStatusColumn={showStatusColumn}
                  onOpenProfile={handleOpenProfile}
                  columnVisibilityModel={columnVisibilityModel}
                />
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!hasActivities && !isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="text-neutral-600 text-base font-medium">
              No activities
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {showLoadingOverlay && <PulseLoading />}
      </div>

      {/* Footer */}
      <TableFooter
        rowCount={data?.count || 0}
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

    </div>
  );
};

export default Activity;
